// Voice editor — 4. Smart limits section. Extracted verbatim from
// `VoiceEditorPage.tsx` (no behavior change).

import { useT } from '@barel/i18n';
import { FieldAssembly, Input, Switch } from '@barel/ui';

import { SectionShell, type SectionPropsWithVoice } from './SectionShell';

export function SmartLimitsSection({ voice, flush }: SectionPropsWithVoice) {
  const t = useT('voice');
  const nightWindowError =
    voice.nightSend && voice.nightStartHour === voice.nightEndHour
      ? t('smartLimits.nightWindowError')
      : undefined;

  return (
    <SectionShell
      id="voice-smart-limits"
      title={t('smartLimits.title')}
      subtitle={t('smartLimits.subtitle')}
    >
      <div className="flex flex-col gap-4">
        <div className="rounded-xl border border-default bg-bg-elevated px-4 py-3">
          <div className="text-2xs font-semibold uppercase tracking-wider text-secondary">
            {t('smartLimits.lockedTitle')}
          </div>
          <ul className="mt-2 flex flex-col gap-1 text-xs text-secondary">
            <li>✓ {t('smartLimits.lockedDelays')}</li>
            <li>✓ {t('smartLimits.lockedDayWindow')}</li>
            <li>✓ {t('smartLimits.lockedDailyCap')}</li>
            <li>✓ {t('smartLimits.lockedVariation')}</li>
          </ul>
        </div>
        <FieldAssembly
          label={t('smartLimits.weekendLabel')}
          description={t('smartLimits.weekendSub')}
          asChild
        >
          <Switch
            aria-label={t('smartLimits.weekendLabel')}
            checked={voice.weekendSend}
            onCheckedChange={(next: boolean) => {
              void flush({ weekendSend: next });
            }}
            data-testid="voice-weekend-switch"
          />
        </FieldAssembly>
        <FieldAssembly
          label={t('smartLimits.nightLabel')}
          description={t('smartLimits.nightSub')}
          asChild
        >
          <Switch
            aria-label={t('smartLimits.nightLabel')}
            checked={voice.nightSend}
            onCheckedChange={(next: boolean) => {
              void flush({ nightSend: next });
            }}
            data-testid="voice-night-switch"
          />
        </FieldAssembly>
        {voice.nightSend ? (
          <div className="grid grid-cols-2 gap-3">
            <FieldAssembly label={t('smartLimits.nightStartLabel')} error={nightWindowError}>
              <Input
                type="number"
                min={0}
                max={23}
                value={voice.nightStartHour}
                onChange={(event) => {
                  const next = clampHour(event.target.value);
                  void flush({ nightStartHour: next });
                }}
                aria-label={t('smartLimits.nightStartLabel')}
                data-testid="voice-night-start"
              />
            </FieldAssembly>
            <FieldAssembly label={t('smartLimits.nightEndLabel')} error={nightWindowError}>
              <Input
                type="number"
                min={0}
                max={23}
                value={voice.nightEndHour}
                onChange={(event) => {
                  const next = clampHour(event.target.value);
                  void flush({ nightEndHour: next });
                }}
                aria-label={t('smartLimits.nightEndLabel')}
                data-testid="voice-night-end"
              />
            </FieldAssembly>
          </div>
        ) : null}
      </div>
    </SectionShell>
  );
}

function clampHour(raw: string | number): number {
  const num = typeof raw === 'number' ? raw : Number.parseInt(raw, 10);
  if (!Number.isFinite(num)) return 0;
  return Math.max(0, Math.min(23, Math.floor(num)));
}
